export interface AuthDto {
  email: string;
  password: string;
}

export interface BooleanResponse {
  success: boolean;
}

export interface ContactDto {
  name: string;
  email: string;
  message: string;
}

export interface CrawlerResponseDto {
  /** @nullable */
  id: string | null;
  /** @nullable */
  requestId?: string | null;
}

export interface CrawlerSettingsAdvancedInputDto {
  isAsync?: boolean;
  includeSubDomains?: boolean;
  includeExternal?: boolean;
  includeBroken?: boolean;
  includeOrphan?: boolean;
  includeImages?: boolean;
  includeFiles?: boolean;
  retries?: number;
  parse?: boolean;
  saveHtml?: boolean;
  saveScreenshot?: boolean;
  userAgent?: CrawlerSettingsAdvancedInputDtoUserAgentItem[];
}

export enum CrawlerSettingsAdvancedInputDtoUserAgentItem {
  "Mozilla/50_(Windows_NT_100;_Win64;_x64)_AppleWebKit/53736_(KHTML,_like_Gecko)_Chrome/121000_Safari/53736" = "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/121.0.0.0 Safari/537.36",
  "Mozilla/50_(Macintosh;_Intel_Mac_OS_X_10_15_7)_AppleWebKit/53736_(KHTML,_like_Gecko)_Chrome/121000_Safari/53736" = "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/121.0.0.0 Safari/537.36",
  "Mozilla/50_(X11;_Linux_x86_64)_AppleWebKit/53736_(KHTML,_like_Gecko)_Chrome/121000_Safari/53736" = "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/121.0.0.0 Safari/537.36",
  "Mozilla/50_(iPhone;_CPU_iPhone_OS_17_3_like_Mac_OS_X)_AppleWebKit/605115_(KHTML,_like_Gecko)_CriOS/12106167138_Mobile/15E148_Safari/6041" = "Mozilla/5.0 (iPhone; CPU iPhone OS 17_3 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) CriOS/121.0.6167.138 Mobile/15E148 Safari/604.1",
  "Mozilla/50_(Linux;_Android_10;_K)_AppleWebKit/53736_(KHTML,_like_Gecko)_Chrome/12106167143_Mobile_Safari/53736" = "Mozilla/5.0 (Linux; Android 10; K) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/121.0.6167.143 Mobile Safari/537.36",
  "Mozilla/50_(Windows_NT_100;_Win64;_x64;_rv:1220)_Gecko/20100101_Firefox/1220" = "Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:122.0) Gecko/20100101 Firefox/122.0",
  "Mozilla/50_(Macintosh;_Intel_Mac_OS_X_143;_rv:1220)_Gecko/20100101_Firefox/1220" = "Mozilla/5.0 (Macintosh; Intel Mac OS X 14.3; rv:122.0) Gecko/20100101 Firefox/122.0",
  "Mozilla/50_(X11;_Ubuntu;_Linux_x86_64;_rv:1220)_Gecko/20100101_Firefox/1220" = "Mozilla/5.0 (X11; Ubuntu; Linux x86_64; rv:122.0) Gecko/20100101 Firefox/122.0",
  "Mozilla/50_(iPhone;_CPU_iPhone_OS_14_3_like_Mac_OS_X)_AppleWebKit/605115_(KHTML,_like_Gecko)_FxiOS/1220_Mobile/15E148_Safari/605115" = "Mozilla/5.0 (iPhone; CPU iPhone OS 14_3 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) FxiOS/122.0 Mobile/15E148 Safari/605.1.15",
  "Mozilla/50_(Android_14;_Mobile;_rv:1220)_Gecko/1220_Firefox/1220" = "Mozilla/5.0 (Android 14; Mobile; rv:122.0) Gecko/122.0 Firefox/122.0",
}

export interface CrawlerSettingsInputDto {
  id?: string;
  url: string;
  specificRoute?: boolean;
  onePage?: boolean;
  advanced?: CrawlerSettingsAdvancedInputDto;
}

export interface CreateUserDto {
  locale?: string;
  name: string;
  email: string;
  password: string;
  refreshToken?: string;
  pin?: string;
  pinExpiration?: string;
}

export interface ForgotPasswordDto {
  locale?: string;
  email: string;
}

export type GetExportWithFormatWithId200 = { [key: string]: unknown };

export type GetExportWithFormatWithIdParams = {
  includeDomain: boolean;
};

export type GetFinishedParams = {
  id: string;
};

export interface PinResponseDto {
  expiresAt: string;
}

export interface PinVerifyDto {
  locale?: string;
  email: string;
  pin: string;
}

export interface ResendPinDto {
  locale?: string;
  email: string;
}

export interface ResetPasswordDto {
  locale?: string;
  token: string;
  password: string;
}

export interface SendCustomEmailDto {
  to: string;
  subject: string;
  html: string;
  text: string;
}

export interface SendSiteContactEmailDto {
  data: SendSiteContactEmailDtoData;
  token: string;
}

export type SendSiteContactEmailDtoData = { [key: string]: unknown };

export interface SendTemplateEmailDto {
  /**
   * @minLength 2
   * @maxLength 2
   */
  locale?: string;
  template: SendTemplateEmailDtoTemplate;
  to: string;
  data: SendTemplateEmailDtoData;
  override: SendTemplateEmailDtoOverride;
  attachments: string[];
}

export type SendTemplateEmailDtoData = { [key: string]: unknown };

export type SendTemplateEmailDtoOverride = { [key: string]: unknown };

export enum SendTemplateEmailDtoTemplate {
  "pin-verification" = "pin-verification",
  "forgot-password" = "forgot-password",
  "site-contact" = "site-contact",
}

export interface SiteDto {
  id: string;
  userId: string;
  url: string;
  pages?: SitePageDto;
  finished: boolean;
  aborted: boolean;
  settings: CrawlerSettingsInputDto;
  pageCount?: number;
}

export interface SitePageDto {
  total: number;
  count: number;
  offset: number;
  limit: number;
  items: SitePageItemDto[];
  desc?: number;
}

export interface SitePageItemDto {
  title: string;
  url: string;
  originalUrl?: string;
  status?: SitePageItemDtoStatus;
  type: SitePageItemDtoType;
}

export type SitePageItemDtoStatus = {
  code: number;
  statusText?: string;
};

export enum SitePageItemDtoType {
  Internal = "Internal",
  External = "External",
  Orphan = "Orphan",
  File = "File",
  Image = "Image",
}

export interface StatusResponseDto {
  success: boolean;
}

export interface TokenResponseDto {
  accessToken: string;
  refreshToken: string;
}

export interface UpdateUserDataDto {
  email: string;
  name?: string;
  password?: string;
}

export interface UpdateUserDto {
  confirmed?: boolean;
  status?: UpdateUserDtoStatus;
  pin?: string;
  pinExpiration?: string;
  resetPasswordToken?: string;
  resetPasswordTokenExpires?: string;
}

export type UpdateUserDtoStatus = { [key: string]: unknown };

export interface UserDto {
  name: string;
  userId: string;
  email: string;
  refreshToken: string;
  roles: string[];
  confirmed: boolean;
  status: UserDtoStatus;
}

export enum UserDtoStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}
